import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import memoize from 'memoize-one';

import {
  indexOtherRequestTypeRequest,
  deleteOtherRequestTypeRequest,
  exportOtherRequestTypeRequest,
  importOtherRequestTypeRequest,
  importTemplateOtherRequestTypeRequest,
  preImportOtherRequestTypeRequest,
  destroyMultipleOtherRequestTypeRequest,
  updateStatusMultipleOtherRequestTypeRequest
} from '../../requests/otherRequestTypes';
import {
  ActiveDot,
  ComponentDataTable,
  DefaultModal,
  BasicActionBtns,
  ImportActions,
  ImportModal,
  SimpleCenteredModal,
  SettingsMassiveActions
} from '../../components';
import { sendAlert } from '../../actions/utils';

import { downloadFile } from '../../services/utils';

const columns = memoize(clickHandler => [
  {
    name: 'NOMBRE',
    selector: 'name',
    sortable: true,
    grow: '1'
  },
  {
    name: 'ACTIVO',
    selector: 'is_active',
    cell: item => <ActiveDot item={{ active: item.is_active }} />,
    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: 'ACCIONES',
    cell: item => <BasicActionBtns item={item} resource="OtherRequestType" clickHandler={clickHandler} />,
    ignoreRowClick: true,
    allowOverflow: false,
    button: true,
    grow: '1',
    minWidth: '123px'
  }
]);

const OtherRequestTypeDataTable = ({ moreData, setMoreData, editOtherRequestType, showOtherRequestType }) => {
  const [amount, setAmount] = useState(0);
  const [modalBody, setModalBody] = useState('');
  const [modalItem, setModalItem] = useState({});
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [query, setQuery] = useState({});
  const [onRequest, setOnRequest] = useState(true);
  const [otherRequestTypes, setOtherRequestTypes] = useState([]);
  const [simpleModalShow, setSimpleModalShow] = useState(false);
  const [modalAction, setModalAction] = useState(() => null);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState('');

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };

  const dispatch = useDispatch();

  const handleSuccessIndex = response => {
    const { data, metadata } = response.data;
    setOtherRequestTypes(data);
    setAmount(metadata.amount);
    setOnRequest(false);
  };

  const handleRequest = async params => {
    setOnRequest(false);
    setQuery(params);
    indexOtherRequestTypeRequest({
      dispatch,
      params,
      successCallback: handleSuccessIndex
    });
  };

  const handleModalClose = () => {
    setModalShow(false);
    setSimpleModalShow(false);
    setOnRequest(false);
  };

  const handleSuccessRemove = message => {
    handleModalClose();
    dispatch(sendAlert({ kind: 'success', message }));
    setMoreData(!moreData);
  };

  const handleFailureRequest = error => {
    const { response } = error;
    handleModalClose();
    dispatch(sendAlert({ kind: 'error', message: response?.data?.message }));
  };

  const removeOtherRequestType = item => {
    const { id } = item;
    setOnRequest(true);
    deleteOtherRequestTypeRequest(id, {
      dispatch,
      successCallback: handleSuccessRemove('Tipo de solicitud eliminado correctamente'),
      failureCallback: handleFailureRequest,
      callback: () => {
        handleModalClose();
      }
    });
  };

  const handleSuccessDownload = response => {
    downloadFile(response);
    setOnRequest(false);
    setModalShow(false);
  };

  const handleExportRequest = () => {
    setOnRequest(true);
    exportOtherRequestTypeRequest({
      dispatch,
      params: { ...query, paginate: false },
      successCallback: handleSuccessDownload,
      failureCallback: handleFailureRequest
    });
  };

  const handleButtonClick = (item, action) => {
    switch (action) {
      case 'show':
        showOtherRequestType(item);
        break;
      case 'edit':
        editOtherRequestType(item);
        break;
      case 'destroy':
        setModalTitle('Eliminar Tipo de Solicitud');
        setModalShow(true);
        setModalItem(item);
        setModalBody('¿Estás seguro que deseas eliminar este tipo de solicitud?');
        setModalAction(() => removeOtherRequestType);
        break;
      default:
        console.log('Error: Action not found');
    }
  };

  const massiveDeleteOtherRequestType = () => {
    const otherRequestTypesSelected = selectedRows.map(item => item.id);
    setOnRequest(true);
    destroyMultipleOtherRequestTypeRequest({
      params: { ids: otherRequestTypesSelected },
      dispatch,
      successCallback: handleSuccessRemove('Tipos de solicitud eliminados correctamente'),
      failureCallback: handleFailureRequest,
      callback: () => {
        handleModalClose();
        setOnRequest(false);
        setClearSelectedRows(!clearSelectedRows);
      }
    });
  };

  const massiveUpdateStatusOtherRequestType = (status) => {
    const otherRequestTypesSelected = selectedRows.map(item => item.id);
    setOnRequest(true);
    updateStatusMultipleOtherRequestTypeRequest({
      params: { ids: otherRequestTypesSelected, is_active: status },
      dispatch,
      successCallback: handleSuccessRemove(`Tipos de solicitud ${status ? 'activados' : 'desactivados'} correctamente`),
      callback: () => {
        handleModalClose();
        setOnRequest(false);
        setClearSelectedRows(!clearSelectedRows);
      }
    });
  };

  const handleImportModalExceptions = error => {
    if (error?.response?.status === 422) {
      const alertInfo = <pre>{error?.response?.data?.message}</pre>;
      setModalTitle('Información Relevante');
      setModalShow(true);
      setModalBody(alertInfo);
      setModalAction(() => handleModalClose);
    } else {
      dispatch(sendAlert({ kind: 'error', message: error?.response?.data?.message }));
    }
  };

  const handleMassAction = action => {
    switch (action) {
      case 'import':
        setSimpleModalShow(true);
        break;
      case 'export':
        setModalTitle('Exportar Tipos de Solicitud');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas exportar tipos de solicitud?');
        setModalAction(() => handleExportRequest);
        break;
      case 'massive_destroy':
        setModalTitle('Eliminar tipos de solicitud');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas eliminar los tipos de solicitud seleccionados?');
        setModalAction(() => massiveDeleteOtherRequestType);
        break;
      case 'massive_active':
        setModalTitle('Activar Tipos de Solicitud');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas activar los tipos de solicitud seleccionados?');
        setModalAction(() => () => massiveUpdateStatusOtherRequestType(true));
        break;
      case 'massive_deactivate':
        setModalTitle('Desactivar Tipos de Solicitud');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas desactivar los tipos de solicitud seleccionados?');
        setModalAction(() => () => massiveUpdateStatusOtherRequestType(false));
        break;
      default:
        console.log('Error: Action not found');
    }
  };

  return (
    <>
      <ComponentDataTable
        onRequest={onRequest}
        columns={columns(handleButtonClick)}
        data={otherRequestTypes}
        totalRows={amount}
        selectableRows
        moreData={moreData}
        resourceRequest={handleRequest}
        withMassActions
        massActions={
          <>
            <ImportActions handleClick={handleMassAction} model="OtherRequestType" />
            <SettingsMassiveActions model="OtherRequestType" handleClick={handleMassAction} disabled={!selectedCount} />
          </>
        }
        onRowClicked={item => handleButtonClick(item, 'show')}
        pointerOnHover
        preName="other_request_types"
        clearSelectedRows={clearSelectedRows}
        onSelectedRowsChange={handleSelectedRows}
      />
      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={handleModalClose}
        handleConfirm={() => modalAction(modalItem)}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
        disabled={onRequest}
      />
      <SimpleCenteredModal
        title="Importar Tipos de Solicitud"
        body={
          <ImportModal
            onDropUploaded={preImportOtherRequestTypeRequest}
            handleTemplate={importTemplateOtherRequestTypeRequest}
            hideModal={() => setSimpleModalShow(false)}
            onHide={importOtherRequestTypeRequest}
            updateData={() => setMoreData(!moreData)}
            handleExceptions={handleImportModalExceptions}
          />
        }
        show={simpleModalShow}
        onHide={() => setSimpleModalShow(false)}
      />
    </>
  );
};

export default OtherRequestTypeDataTable;